import { gql, useQuery } from '@apollo/client';
import { type FeatureGateName } from '@orn/global/featureGates/featureGates.js';
import * as React from 'react';

import type { EnabledFeatureGatesQuery, EnabledFeatureGatesQueryVariables } from './__generated__/useFeatureGates.gql';

const ENABLED_FEATURE_GATES_QUERY = gql`
  query EnabledFeatureGates {
    enabledFeatureGates
  }
`;

interface IUseFeatureGatesResult {
  /** O(1) lookup set of currently-enabled gate names for the viewer. */
  flags: Set<string>;
  /** Apollo's loading flag — `true` until the query resolves at least once. */
  loading: boolean;
}

/**
 * Returns the set of feature-gate names the current viewer passes
 * (audience: WEB_CLIENT or ALL_CLIENTS) plus Apollo's loading flag.
 * `cache-and-network` returns the cached set synchronously and refetches
 * in the background, so the kill-switch SLA is bounded by the server's
 * cache TTL plus one round-trip rather than by stacked client + server
 * caches.
 *
 * @returns `{ flags, loading }`.
 */
export const useFeatureGates = (): IUseFeatureGatesResult => {
  const { data, loading } = useQuery<EnabledFeatureGatesQuery, EnabledFeatureGatesQueryVariables>(
    ENABLED_FEATURE_GATES_QUERY,
    { fetchPolicy: 'cache-and-network' }
  );
  const flags = React.useMemo(() => new Set(data?.enabledFeatureGates ?? []), [data]);
  return { flags, loading };
};

/**
 * Returns `true` iff `name` is in the viewer's enabled-gate set.
 * Returns `false` while the bootstrap query is loading and no cached
 * set exists — this is intentional fail-closed UX so a feature stays
 * hidden until the server has confirmed the viewer's eligibility.
 *
 * Dev defaults are resolved server-side (`FeatureGateController` in
 * `@orn/core` consults `FEATURE_GATE_DEV_DEFAULTS` from `@orn/global`
 * when `ORN_ENV === 'dev'` and projects the result into the same
 * `enabledFeatureGates` GraphQL response). The frontend stays dumb —
 * one source of truth, one place to manage dev gates, and Storybook
 * stays predictable because stories control gate state via their MSW
 * handler, not via a parallel client-side override path.
 *
 * `name` is constrained to {@link FeatureGateName} so only members of
 * the central `FeatureGate` registry (`@orn/global/featureGates/featureGates`)
 * are legal at the call site. Passing a bare string literal that has
 * not been registered is a compile error rather than a silent
 * always-false at runtime — see `packages/core/docs/feature-gate-naming.md`
 * for the rationale and broader conventions.
 *
 * @param name Member of the `FeatureGate` registry.
 * @returns `true` if the gate is enabled for the viewer, else `false`.
 */
export const useFeatureGate = (name: FeatureGateName): boolean => useFeatureGates().flags.has(name);
